import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

// ─── Tunable constants ────────────────────────────────────────────────────────
// Max simultaneous PDF.js render tasks. Keeping at 2 reduces memory pressure
// per render cycle; 3+ causes mid-paint cancellations on large docs.
const MAX_CONCURRENT_RENDERS = 2;
// Max pages kept mounted (canvas in memory). At ~3-4 MB per canvas, 12 = ~36-48 MB.
// Critical for large documents (1000+ pages) to prevent 99% RAM usage.
const MAX_RENDERED_PAGES = 12;
// Retry limit per page before giving up and showing a placeholder.
const MAX_RENDER_ATTEMPTS = 3;
// How long (ms) before treating a non-responsive render as silently cancelled.
// PDF.js sometimes cancels render tasks under memory pressure without calling
// onRenderError, leaving a permanently black canvas. This timeout forces a retry.
const RENDER_TIMEOUT_MS = 6000;
// ─────────────────────────────────────────────────────────────────────────────

type RenderWindowConfig = {
  /**
   * How far beyond the scroll-container edge to start rendering pages.
   * Smaller = less memory, larger = smoother fast-scroll.
   */
  rootMarginPx?: number;
  /** Placeholder height while a page is not yet rendered. */
  estimatedPageHeightPx?: number;
  /** Override the global MAX_RENDERED_PAGES cap. */
  maxRenderedPages?: number;
};

type VirtualizedPdfProps = {
  file: string;
  scale: number;
  className?: string;
  pageClassName?: string;
  onLoadSuccess?: (payload: { numPages: number }) => void;
  onLoadError?: () => void;
  onPageWrapperRef?: (pageNumber: number, el: HTMLDivElement | null) => void;
  onCurrentPageChange?: (pageNumber: number) => void;
  currentPage?: number;
  config?: RenderWindowConfig;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

export const VirtualizedPdf: React.FC<VirtualizedPdfProps> = ({
  file,
  scale,
  className,
  pageClassName,
  onLoadSuccess,
  onLoadError,
  onPageWrapperRef,
  onCurrentPageChange,
  currentPage,
  config,
}) => {
  const rootMarginPx          = config?.rootMarginPx          ?? 800;
  const estimatedPageHeightPx = config?.estimatedPageHeightPx ?? 1100;
  const maxRenderedPages      = config?.maxRenderedPages      ?? MAX_RENDERED_PAGES;

  const [numPages, setNumPages] = useState(0);

  // Which page numbers currently have a <Page> canvas mounted.
  const [mountedPages, setMountedPages] = useState<Set<number>>(new Set());

  // Per-page render attempt counter. Used as part of <Page> key so that
  // onRenderError forces a completely fresh canvas remount.
  const [renderAttempts, setRenderAttempts] = useState<Record<number, number>>({});

  // ── Render queue (mutable refs — no re-render needed) ─────────────────────
  // Pages waiting for a free render slot.
  const renderQueueRef    = useRef<Set<number>>(new Set());
  // Pages whose <Page> is mounted and actively rendering right now.
  const activeRendersRef  = useRef<Set<number>>(new Set());
  // Pages that have finished rendering successfully (canvas complete).
  const renderedPagesRef  = useRef<Set<number>>(new Set());
  // Mirrors renderAttempts state so handleRenderError reads current values
  // without stale-closure issues inside callbacks.
  const renderAttemptsRef = useRef<Record<number, number>>({});
  // Per-page safety timeout handles. If onRenderSuccess/Error never fires
  // within RENDER_TIMEOUT_MS, the timeout forces handleRenderError so the
  // black canvas is replaced with a fresh one.
  const renderTimersRef   = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // Stable ref to handleRenderError so drainQueue's timeout closure can call
  // it without creating a circular useCallback dependency.
  const handleRenderErrorRef = useRef<(n: number) => void>(() => {});

  // ── IntersectionObserver / scroll state ───────────────────────────────────
  const visiblePagesRef     = useRef<Set<number>>(new Set());
  const rafRef              = useRef<number | null>(null);
  const lastCurrentPageRef  = useRef<number>(1);
  // Mutable copy for queue prioritisation — avoids rebuilding callbacks on
  // every currentPage prop change.
  const currentPageRef      = useRef<number>(currentPage ?? 1);
  const ioRef               = useRef<IntersectionObserver | null>(null);
  const rootElRef           = useRef<HTMLElement | null>(null);
  const elementToPageRef    = useRef<WeakMap<Element, number>>(new WeakMap());

  // Stable refs for prop-callbacks so useCallback deps stay empty.
  const onCurrentPageChangeRef = useRef(onCurrentPageChange);
  const onPageWrapperRefRef    = useRef(onPageWrapperRef);
  onCurrentPageChangeRef.current = onCurrentPageChange;
  onPageWrapperRefRef.current    = onPageWrapperRef;

  // Keep currentPageRef in sync without rebuilding every stable callback.
  useEffect(() => {
    currentPageRef.current = currentPage ?? 1;
  }, [currentPage]);

  // ── Full reset when file changes ───────────────────────────────────────────
  useEffect(() => {
    renderTimersRef.current.forEach(t => clearTimeout(t));
    renderTimersRef.current.clear();
    setMountedPages(new Set());
    setRenderAttempts({});
    setNumPages(0);
    renderQueueRef.current    = new Set();
    activeRendersRef.current  = new Set();
    renderedPagesRef.current  = new Set();
    renderAttemptsRef.current = {};
    visiblePagesRef.current   = new Set();
    lastCurrentPageRef.current = 1;
    if (ioRef.current) {
      ioRef.current.disconnect();
      ioRef.current = null;
    }
    rootElRef.current        = null;
    elementToPageRef.current = new WeakMap();
  }, [file]);

  // ── Drain the render queue ─────────────────────────────────────────────────
  // Promotes up to MAX_CONCURRENT_RENDERS pages from the queue to mounted,
  // prioritising pages closest to the current scroll position.
  // Starts a per-page safety timeout so a silently-cancelled render (black
  // canvas with no callback) is retried automatically.
  const drainQueue = useCallback(() => {
    const slots = MAX_CONCURRENT_RENDERS - activeRendersRef.current.size;
    if (slots <= 0 || renderQueueRef.current.size === 0) return;

    const cur = currentPageRef.current;
    const sorted = Array.from(renderQueueRef.current).sort(
      (a, b) => Math.abs(a - cur) - Math.abs(b - cur)
    );
    const toStart = sorted.slice(0, slots);
    if (toStart.length === 0) return;

    toStart.forEach(p => {
      renderQueueRef.current.delete(p);
      activeRendersRef.current.add(p);

      // Safety timeout: PDF.js sometimes cancels renders under memory pressure
      // without ever calling onRenderSuccess or onRenderError. The canvas stays
      // permanently black and the slot stays occupied. This timeout forces a
      // retry via handleRenderError so the black canvas is replaced.
      const timer = setTimeout(() => {
        renderTimersRef.current.delete(p);
        if (activeRendersRef.current.has(p)) {
          handleRenderErrorRef.current(p);
        }
      }, RENDER_TIMEOUT_MS);
      renderTimersRef.current.set(p, timer);
    });

    setMountedPages(prev => {
      const next = new Set(prev);
      toStart.forEach(p => next.add(p));
      return next;
    });
  }, []);

  // After any mountedPages change, drain remaining items in the queue.
  // This handles: scale-change re-queue, retry after error, and initial load.
  useEffect(() => {
    if (renderQueueRef.current.size > 0) drainQueue();
  }, [mountedPages, drainQueue]);

  // ── Scale change: cancel all in-flight renders, re-queue nearby pages ──────
  // When scale changes, <Page> keys change so react-pdf cancels their render
  // tasks. We clear active tracking and re-queue visible + nearby pages so
  // they start rendering in priority order instead of all at once.
  //
  // numPages is included as a dep so this effect defers until the PDF has
  // actually loaded. Without this guard, a parent zoom-reset that happens
  // in a separate render cycle (after file change) would start render timers
  // for pages before react-pdf has any page data, burning through
  // renderAttemptsRef counters before any real rendering occurs.
  const prevScaleRef = useRef(scale);
  useEffect(() => {
    if (prevScaleRef.current === scale) return;
    if (numPages <= 0) return; // PDF not loaded yet — defer until onLoadSuccess
    prevScaleRef.current = scale;

    renderTimersRef.current.forEach(t => clearTimeout(t));
    renderTimersRef.current.clear();

    const cur = currentPageRef.current;
    const nearby = new Set<number>();
    visiblePagesRef.current.forEach(p => nearby.add(p));
    for (let p = Math.max(1, cur - 3); p <= Math.min(numPages, cur + 8); p++) nearby.add(p);

    activeRendersRef.current = new Set();
    renderedPagesRef.current = new Set();
    renderQueueRef.current   = nearby;

    // Unmount everything — the useEffect above will drain the queue.
    setMountedPages(new Set());
  }, [scale, numPages]);

  // ── Prune rendered pages far from viewport to keep memory bounded ──────────
  const pruneRenderedPages = useCallback(() => {
    if (renderedPagesRef.current.size <= maxRenderedPages) return;

    const cur = currentPageRef.current;
    const prunable = Array.from(renderedPagesRef.current)
      .filter(p => !visiblePagesRef.current.has(p) && !activeRendersRef.current.has(p))
      .sort((a, b) => Math.abs(b - cur) - Math.abs(a - cur));

    const overBy = renderedPagesRef.current.size - maxRenderedPages;
    const toRemove = prunable.slice(0, overBy);
    if (toRemove.length === 0) return;

    toRemove.forEach(p => renderedPagesRef.current.delete(p));
    setMountedPages(prev => {
      const next = new Set(prev);
      toRemove.forEach(p => next.delete(p));
      return next;
    });
  }, [maxRenderedPages]);

  // ── Enqueue a page (no-op if already in queue / active / rendered) ─────────
  const enqueuePage = useCallback((pageNumber: number) => {
    if (
      activeRendersRef.current.has(pageNumber) ||
      renderedPagesRef.current.has(pageNumber) ||
      renderQueueRef.current.has(pageNumber)
    ) return;

    // If the page was previously abandoned (attempts exhausted), reset the
    // counter so this IO-triggered re-enqueue gets a full set of retries.
    // Without this, the first failure on the re-enqueue would immediately
    // give up (next > MAX_RENDER_ATTEMPTS) instead of retrying.
    if ((renderAttemptsRef.current[pageNumber] ?? 0) > MAX_RENDER_ATTEMPTS) {
      renderAttemptsRef.current[pageNumber] = 0;
      setRenderAttempts(prev => {
        const next = { ...prev };
        delete next[pageNumber];
        return next;
      });
    }

    renderQueueRef.current.add(pageNumber);
    drainQueue();
  }, [drainQueue]);

  // ── Pre-load window around currentPage ────────────────────────────────────
  useEffect(() => {
    if (!currentPage || numPages <= 0) return;
    const from = clamp(currentPage - 2, 1, numPages);
    const to   = clamp(currentPage + 5, 1, numPages);
    for (let p = from; p <= to; p++) enqueuePage(p);
    pruneRenderedPages();
  }, [currentPage, numPages, enqueuePage, pruneRenderedPages]);

  // ── Render success / error callbacks ─────────────────────────────────────
  const handleRenderSuccess = useCallback((pageNumber: number) => {
    // Guard against stale callbacks (e.g. slow render completes after the
    // safety timeout already called handleRenderError for this slot).
    if (!activeRendersRef.current.has(pageNumber)) return;

    const timer = renderTimersRef.current.get(pageNumber);
    if (timer !== undefined) {
      clearTimeout(timer);
      renderTimersRef.current.delete(pageNumber);
    }

    activeRendersRef.current.delete(pageNumber);
    renderedPagesRef.current.add(pageNumber);
    pruneRenderedPages();
    drainQueue();
  }, [pruneRenderedPages, drainQueue]);

  const handleRenderError = useCallback((pageNumber: number) => {
    // Guard against stale callbacks (e.g. react-pdf fires onRenderError for
    // the old Page unmount AFTER the timeout already handled this slot).
    if (!activeRendersRef.current.has(pageNumber)) return;

    const timer = renderTimersRef.current.get(pageNumber);
    if (timer !== undefined) {
      clearTimeout(timer);
      renderTimersRef.current.delete(pageNumber);
    }

    activeRendersRef.current.delete(pageNumber);

    const cur  = renderAttemptsRef.current[pageNumber] ?? 0;
    const next = cur + 1;
    renderAttemptsRef.current[pageNumber] = next;

    if (next <= MAX_RENDER_ATTEMPTS) {
      // Re-queue: fresh canvas will be created when page remounts.
      renderQueueRef.current.add(pageNumber);
    }

    // Bump state so <Page key> changes → fresh canvas on next mount.
    setRenderAttempts(prev => ({ ...prev, [pageNumber]: next }));

    // Unmount so the failed/black canvas is removed; drainQueue will re-mount
    // if the page is still in the queue, or leave it as placeholder if
    // MAX_RENDER_ATTEMPTS was reached.
    setMountedPages(prev => {
      const n = new Set(prev);
      n.delete(pageNumber);
      return n;
    });
  }, []);

  // Keep the error ref current so the drainQueue timeout closure always calls
  // the latest version without making drainQueue depend on handleRenderError.
  handleRenderErrorRef.current = handleRenderError;

  // ── IntersectionObserver ──────────────────────────────────────────────────
  const scheduleCurrentPageCompute = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      if (visiblePagesRef.current.size === 0) return;
      const sorted = Array.from(visiblePagesRef.current).sort((a, b) => a - b);
      const next = sorted[0] ?? 1;
      if (next !== lastCurrentPageRef.current) {
        lastCurrentPageRef.current = next;
        onCurrentPageChangeRef.current?.(next);
      }
    });
  }, []);

  const ensureObserver = useCallback(
    (root: HTMLElement | null) => {
      const rootChanged = rootElRef.current !== root;
      if (!ioRef.current || rootChanged) {
        ioRef.current?.disconnect();
        rootElRef.current = root;
        ioRef.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const pageNumber = elementToPageRef.current.get(entry.target);
              if (!pageNumber) continue;
              if (entry.isIntersecting) {
                visiblePagesRef.current.add(pageNumber);
                enqueuePage(pageNumber);
              } else {
                visiblePagesRef.current.delete(pageNumber);
              }
            }
            scheduleCurrentPageCompute();
          },
          {
            root,
            threshold: 0.01,
            rootMargin: `${rootMarginPx}px 0px ${rootMarginPx}px 0px`,
          }
        );
      }
      return ioRef.current;
    },
    [rootMarginPx, enqueuePage, scheduleCurrentPageCompute]
  );

  // Stable across renders — avoids re-attaching IO on every parent re-render,
  // which was the primary cause of IO-event storms leading to blank pages.
  const attachObserver = useCallback(
    (pageNumber: number, el: HTMLDivElement | null) => {
      onPageWrapperRefRef.current?.(pageNumber, el);
      if (!el) return;
      const root = el.closest('[data-pdf-scroll-root]') as HTMLElement | null;
      const io = ensureObserver(root);
      elementToPageRef.current.set(el, pageNumber);
      io.observe(el);
      return () => {
        visiblePagesRef.current.delete(pageNumber);
        try { io.unobserve(el); } catch { /* ignore */ }
      };
    },
    [ensureObserver]
  );

  // Global cleanup on unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      ioRef.current?.disconnect();
      renderTimersRef.current.forEach(t => clearTimeout(t));
      renderTimersRef.current.clear();
    };
  }, []);

  const pages = useMemo(
    () => (numPages > 0 ? Array.from({ length: numPages }, (_, i) => i + 1) : []),
    [numPages]
  );

  return (
    <div className={className}>
      <Document
        key={file}
        file={file}
        onLoadSuccess={({ numPages: n }) => {
          setNumPages(n || 0);
          onLoadSuccess?.({ numPages: n || 0 });
        }}
        onLoadError={onLoadError}
        loading={null}
      >
        {pages.map((pageNumber) => (
          <PdfPageWrapper
            key={pageNumber}
            pageNumber={pageNumber}
            isMounted={mountedPages.has(pageNumber)}
            scale={scale}
            attempts={renderAttempts[pageNumber] ?? 0}
            estimatedPageHeightPx={estimatedPageHeightPx}
            pageClassName={pageClassName}
            attachObserver={attachObserver}
            onRenderSuccess={handleRenderSuccess}
            onRenderError={handleRenderError}
          />
        ))}
      </Document>
    </div>
  );
};

// ─── PdfPageWrapper ───────────────────────────────────────────────────────────
// Memoised so only the specific pages that change state re-render.
// All callback props are stable (useCallback with empty / stable deps above),
// so React.memo bails out for the ~1995 unchanged pages on each state update.
const PdfPageWrapper = React.memo(function PdfPageWrapper({
  pageNumber,
  isMounted,
  scale,
  attempts,
  estimatedPageHeightPx,
  pageClassName,
  attachObserver,
  onRenderSuccess,
  onRenderError,
}: {
  pageNumber: number;
  isMounted: boolean;
  scale: number;
  attempts: number;
  estimatedPageHeightPx: number;
  pageClassName?: string;
  attachObserver: (n: number, el: HTMLDivElement | null) => void | (() => void);
  onRenderSuccess: (n: number) => void;
  onRenderError: (n: number) => void;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);

  // Show a white overlay + spinner while the canvas is being painted by PDF.js.
  // useLayoutEffect runs synchronously before the browser paints, so the
  // overlay appears in the same frame as the <Page> mount — the black canvas
  // is never visible to the user.
  const [isRendering, setIsRendering] = useState(false);
  useLayoutEffect(() => {
    setIsRendering(isMounted);
  }, [isMounted, attempts, scale]);

  // Intercept parent callbacks to clear the loading overlay on completion.
  // Both parent callbacks are stable useCallback refs, so these wrappers are
  // also stable — React.memo's bail-out is preserved.
  const wrappedOnSuccess = useCallback(() => {
    setIsRendering(false);
    onRenderSuccess(pageNumber);
  }, [onRenderSuccess, pageNumber]);

  const wrappedOnError = useCallback(() => {
    setIsRendering(false);
    onRenderError(pageNumber);
  }, [onRenderError, pageNumber]);

  // Attach IO exactly once per element mount. Using useEffect (not an inline
  // ref callback) means it only fires on mount/unmount, not on every parent
  // re-render — the previous inline pattern caused IO storms on fast scroll.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const cleanup = attachObserver(pageNumber, el);
    return () => {
      if (typeof cleanup === 'function') cleanup();
      attachObserver(pageNumber, null);
    };
  }, [pageNumber, attachObserver]);

  // Release canvas GPU/backing-store memory immediately when a page is unmounted
  // (pruned from the render window). Without this Chrome holds the pixel buffer
  // until GC, which for large PDFs (1000+ pages) causes memory to spike to 99%.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.removedNodes.forEach((node) => {
          if (node instanceof Element) {
            const canvases = node.tagName === 'CANVAS'
              ? [node as HTMLCanvasElement]
              : Array.from(node.querySelectorAll<HTMLCanvasElement>('canvas'));
            canvases.forEach((c) => { c.width = 0; c.height = 0; });
          }
        });
      }
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  const placeholder = (
    <div
      className={pageClassName}
      style={{
        height: `${Math.max(220, estimatedPageHeightPx * scale)}px`,
        width: 'min(100%, 980px)',
        background: 'white',
        borderRadius: 2,
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }}
    />
  );

  return (
    <div
      ref={elRef}
      data-page-number={pageNumber}
      style={{
        position: 'relative',
        minHeight: `${Math.max(220, estimatedPageHeightPx * scale)}px`,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {isMounted ? (
        <Page
          // attempts in key forces a completely fresh canvas on retry,
          // replacing the blank canvas left by a cancelled render task.
          key={`p-${pageNumber}-${scale}-${attempts}`}
          pageNumber={pageNumber}
          scale={scale}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          canvasBackground="white"
          className={pageClassName}
          loading={placeholder}
          error={placeholder}
          noData={placeholder}
          onRenderSuccess={wrappedOnSuccess}
          onRenderError={wrappedOnError}
          onLoadError={wrappedOnError}
        />
      ) : (
        placeholder
      )}

      {/* Loading overlay — covers the black initial canvas while PDF.js paints.
          Disappears the moment onRenderSuccess fires. */}
      {isRendering && isMounted && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            zIndex: 2,
            borderRadius: 2,
          }}
        >
          <div
            className="animate-spin"
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '3px solid #e5e7eb',
              borderTopColor: '#6b7280',
            }}
          />
        </div>
      )}
    </div>
  );
});

export default VirtualizedPdf;
